/**
 * /symbol/: set up this browser's PageID symbol and words as a 3-step form (draw → words → done), a quick
 * tutorial on checking a site, and the returning view (change words, start over). SPEC.md §12.6.
 * The cookie is set only when step 2 saves (POST /api/symbol {action: setup|reset, phrase, draw}); a draw is a
 * signed ticket from the server, so the symbol stays random (the shuffle below is decoration).
 */
import { SYMBOL_COUNT, symbolSvg } from '../../../packages/core/src/symbols.ts';
import { counterText, decoyFor, markTourSeen, phraseState, progressText, redrawLabel, rollDelays, rollSymbols, swipeStep, TOUR_CARDS, tourMove, tourSeen, type Step } from './setup-logic.ts';

type Sym = { n: number; name: string; svg: string };
type View = { set: boolean; again?: boolean; symbol?: Sym; phrase?: string; issued?: string; error?: string; code?: string };
type DrawRes = { symbol: Sym; draw: string; redrawsLeft: number; error?: string; code?: string };

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
const storage = (() => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
})();
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const TICK = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.2 4.2L19 7" fill="none" stroke="currentColor" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const CROSS = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round"/></svg>';
const MARK = '<svg viewBox="0 0 96 96" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" d="M29 20h22.5a18 18 0 0 1 0 36H43v20H29zM43 32.5v11h8a5.5 5.5 0 0 0 0-11z"/></svg>';

// ---- state --------------------------------------------------------------------------------------------------
const st = {
  view: 'loading' as 'loading' | 'setup' | 'tour' | 'mine',
  mode: 'setup' as 'setup' | 'reset',
  step: 1 as Step,
  draw: null as DrawRes | null,
  revealing: false,
  tour: 0,
  current: null as View | null,
};
/** For the label-sim (e2e): the page's state, read-only. */
(window as unknown as { pageidSetup: unknown }).pageidSetup = {
  get view() { return st.view; },
  get mode() { return st.mode; },
  get step() { return st.step; },
  get revealing() { return st.revealing; },
  get redrawsLeft() { return st.draw?.redrawsLeft ?? null; },
  get symbol() { return st.draw?.symbol.n ?? null; },
  get tour() { return st.tour; },
};

async function api<T = View>(body?: object): Promise<T> {
  const r = await fetch('/api/symbol', body ? { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body), credentials: 'same-origin' } : { credentials: 'same-origin', cache: 'no-store' });
  const j = (await r.json()) as T & { error?: string; code?: string };
  if (!r.ok) throw Object.assign(new Error(j.error ?? `HTTP ${r.status}`), { code: j.code });
  return j;
}

function showView(v: typeof st.view) {
  st.view = v;
  for (const [id, name] of [['s-loading', 'loading'], ['s-setup', 'setup'], ['s-tour', 'tour'], ['s-mine', 'mine']] as const) $(id).hidden = name !== v;
  document.body.dataset.state = `s-${v}`;
}

/** Moves into `el` with a short slide (transform/opacity); none for reduced motion. */
function enter(el: HTMLElement, dir: 1 | -1) {
  el.classList.remove('in-fwd', 'in-back');
  if (reduced()) return;
  void el.offsetWidth;
  el.classList.add(dir > 0 ? 'in-fwd' : 'in-back');
}
const focusEl = (el: HTMLElement | null) => el?.focus({ preventScroll: false });

// ---- the preview pill (drawn by this page: what the genuine pill looks like) ---------------------------------
function pill(o: { svg: string | null; words: string | null; line2: string; fake?: boolean; label: string }): HTMLElement {
  const p = document.createElement('div');
  p.className = `pv-pill${o.fake ? ' fake' : ''}`;
  p.setAttribute('role', 'img');
  p.setAttribute('aria-label', o.label);
  const slot = document.createElement('span');
  slot.className = 'pv-slot';
  const face = document.createElement('span');
  face.className = 'pv-face';
  face.innerHTML = o.svg ?? '';
  const badge = document.createElement('span');
  badge.className = 'pv-badge';
  badge.innerHTML = TICK;
  slot.append(face, badge);
  const txt = document.createElement('span');
  txt.className = 'pv-txt';
  const l1 = document.createElement('span');
  l1.className = 'pv-l1';
  if (o.words) l1.textContent = `“${o.words}”`;
  else l1.innerHTML = 'Verified by <span class="wm">Page<b>ID</b></span>';
  const l2 = document.createElement('span');
  l2.className = 'pv-l2';
  l2.textContent = o.line2;
  txt.append(l1, l2);
  const btn = document.createElement('span');
  btn.className = 'pv-btn';
  btn.innerHTML = `<span class="vb-mark">${MARK}</span><span class="vb-wm">Page<b>ID</b></span><span>Verify</span><span>↗</span>`;
  p.append(slot, txt, btn);
  if (o.fake) {
    const x = document.createElement('span');
    x.className = 'pv-x';
    x.innerHTML = CROSS;
    p.append(x);
  }
  return p;
}
const realPill = (v: View) => pill({ svg: v.symbol!.svg, words: v.phrase ?? '', line2: '✓ Example Shop · shop.dk', label: `The genuine PageID pill: your symbol, ${v.symbol!.name}, and your words, “${v.phrase}”` });
const fakePill = (mine: number) => pill({ svg: symbolSvg(decoyFor(mine, SYMBOL_COUNT), 30, false), words: null, line2: '✓ a-fake-site.com', fake: true, label: 'A fake pill: a symbol that is not yours, and no words of yours' });

// ---- the step form --------------------------------------------------------------------------------------------
function goStep(n: Step, dir: 1 | -1 = 1) {
  st.step = n;
  for (const k of [1, 2, 3]) $(`step-${k}`).hidden = k !== n;
  const prog = $('wiz-progress');
  prog.setAttribute('aria-valuenow', String(n));
  prog.setAttribute('aria-valuetext', progressText(n));
  $('wiz-count').textContent = `${n} of 3`;
  prog.querySelectorAll('.dots i').forEach((d, i) => d.classList.toggle('on', i < n));
  // Back: step 2 → 1; step 1 only when starting over (back to the returning view); never from step 3.
  $('wiz-back').hidden = n === 3 || (n === 1 && st.mode === 'setup');
  document.body.dataset.step = String(n);
  const el = $(`step-${n}`);
  enter(el, dir);
  if (n === 2) {
    const i = $<HTMLInputElement>('phrase');
    focusEl(i);
    words();
  } else focusEl($(`step-${n}-title`));
}

function startWizard(mode: 'setup' | 'reset', again = false) {
  st.mode = mode;
  st.draw = null;
  $('s-again').hidden = !again;
  $('step-1-title').textContent = mode === 'reset' ? 'Get a new symbol' : 'Get your symbol';
  $('draw-stage').dataset.drawn = '0';
  $('draw-box').innerHTML = '<span class="draw-q">?</span>';
  $('draw-box').className = 'draw-box';
  $('draw-name').textContent = '';
  $('draw-lead').hidden = false;
  $('draw-done').hidden = true;
  $('draw-btn').hidden = false;
  $<HTMLButtonElement>('draw-btn').disabled = false;
  $('to-2').hidden = true;
  $('redraw').hidden = true;
  $('draw-error').textContent = '';
  $<HTMLInputElement>('phrase').value = '';
  $('setup-error').textContent = '';
  showView('setup');
  goStep(1);
}

async function reveal(d: DrawRes) {
  const box = $('draw-box');
  st.revealing = true;
  box.className = 'draw-box rolling';
  const delays = rollDelays(reduced());
  const decoys = rollSymbols(d.symbol.n, delays.length, SYMBOL_COUNT);
  for (let i = 0; i < delays.length; i++) {
    box.innerHTML = symbolSvg(decoys[i], 96, false);
    await sleep(delays[i]);
  }
  box.innerHTML = d.symbol.svg;
  box.dataset.symbol = String(d.symbol.n);
  box.className = reduced() ? 'draw-box landed still' : 'draw-box landed';
  $('draw-name').textContent = cap(d.symbol.name);
  $('draw-stage').dataset.drawn = '1';
  st.revealing = false;
}

function afterDraw() {
  const d = st.draw!;
  $('draw-lead').hidden = true;
  $('draw-done').hidden = false;
  $('draw-btn').hidden = true;
  $('to-2').hidden = false;
  const label = redrawLabel(d.redrawsLeft);
  $('redraw').hidden = !label;
  $('redraw').textContent = label ?? '';
  $<HTMLButtonElement>('redraw').disabled = false;
  $('words-sym').innerHTML = d.symbol.svg;
}

async function draw(again: boolean) {
  const btn = $<HTMLButtonElement>(again ? 'redraw' : 'draw-btn');
  if (st.revealing) return;
  btn.disabled = true;
  $<HTMLButtonElement>('to-2').disabled = true;
  $('draw-error').textContent = '';
  try {
    const d = await api<DrawRes>(again && st.draw ? { action: 'draw', draw: st.draw.draw } : { action: 'draw' });
    st.draw = d;
    $('to-2').hidden = true;
    $('redraw').hidden = true;
    await reveal(d);
    afterDraw();
    $<HTMLButtonElement>('to-2').disabled = false;
    focusEl($('to-2'));
  } catch (x) {
    if ((x as { code?: string }).code === 'no-more-draws' && st.draw) {
      st.draw.redrawsLeft = 0;
      afterDraw();
    } else $('draw-error').textContent = (x as Error).message;
  } finally {
    btn.disabled = false;
    $<HTMLButtonElement>('to-2').disabled = false;
  }
}

function words() {
  const i = $<HTMLInputElement>('phrase');
  $('phrase-count').textContent = counterText(i.value);
  $<HTMLButtonElement>('save-btn').disabled = !phraseState(i.value).ok || !st.draw;
}

async function save(e: Event) {
  e.preventDefault();
  const i = $<HTMLInputElement>('phrase');
  if (!phraseState(i.value).ok || !st.draw) return;
  const btn = $<HTMLButtonElement>('save-btn');
  btn.disabled = true;
  $('setup-error').textContent = '';
  try {
    const v = await api({ action: st.mode === 'reset' ? 'reset' : 'setup', phrase: i.value, draw: st.draw.draw });
    st.current = v;
    renderMine(v);
    const pv = $('done-preview');
    pv.replaceChildren(realPill(v));
    const seen = tourSeen(storage);
    $('to-tour').innerHTML = seen ? 'Finish' : 'Next: how to check a site <span class="muted-on">· 30 s</span>';
    $('done-skip').textContent = seen ? 'How to check a site' : 'Skip';
    $('done-skip').dataset.role = seen ? 'tour' : 'skip';
    $('to-tour').dataset.role = seen ? 'finish' : 'tour';
    goStep(3);
  } catch (x) {
    if ((x as { code?: string }).code === 'draw-expired') {
      startWizard(st.mode);
      $('draw-error').textContent = 'That took a while, so your draw expired. Tap to get your symbol again.';
    } else {
      $('setup-error').textContent = (x as Error).message;
      btn.disabled = false;
    }
  }
}

// ---- the tutorial ---------------------------------------------------------------------------------------------
function openTour() {
  const v = st.current;
  if (!v?.symbol) return;
  markTourSeen(storage);
  $('tour-pill-real').replaceChildren(realPill(v));
  $('tour-pill-fake').replaceChildren(fakePill(v.symbol.n));
  $('tour-v-sym').innerHTML = v.symbol.svg;
  $('tour-v-words').textContent = `“${v.phrase ?? ''}”`;
  showView('tour');
  tourGo(0, 1);
}

function tourGo(i: number, dir: 1 | -1) {
  st.tour = i;
  for (let k = 0; k < TOUR_CARDS; k++) $(`tour-${k + 1}`).hidden = k !== i;
  const prog = $('tour-progress');
  prog.setAttribute('aria-valuenow', String(i + 1));
  prog.setAttribute('aria-valuetext', `Card ${i + 1} of ${TOUR_CARDS}`);
  $('tour-count').textContent = `${i + 1} of ${TOUR_CARDS}`;
  prog.querySelectorAll('.dots i').forEach((d, k) => d.classList.toggle('on', k <= i));
  $<HTMLButtonElement>('tour-prev').disabled = i === 0;
  $('tour-next').textContent = i === TOUR_CARDS - 1 ? 'Done' : 'Next';
  $('tour-skip').hidden = i === TOUR_CARDS - 1;
  document.body.dataset.tour = String(i + 1);
  const card = $(`tour-${i + 1}`);
  enter(card, dir);
  const stage = card.querySelector('.pv-stage');
  if (stage) {
    stage.classList.remove('play');
    if (!reduced()) {
      void (stage as HTMLElement).offsetWidth;
      stage.classList.add('play');
    }
  }
  focusEl($(`tour-${i + 1}-title`));
}

function closeTour() {
  markTourSeen(storage);
  if (st.current) renderMine(st.current);
  showView('mine');
  window.scrollTo(0, 0);
  focusEl($('mine-title'));
}

// ---- the returning view ---------------------------------------------------------------------------------------
function renderMine(v: View) {
  $('sym-big').innerHTML = v.symbol!.svg;
  $('sym-big').dataset.symbol = String(v.symbol!.n);
  $('sym-name').textContent = cap(v.symbol!.name);
  $('sym-phrase').textContent = `“${v.phrase ?? ''}”`;
  $<HTMLInputElement>('phrase2').value = v.phrase ?? '';
  $('phrase2-count').textContent = counterText(v.phrase ?? '');
  $('mine-preview').replaceChildren(realPill(v));
}

function render(v: View) {
  st.current = v;
  if (!v.set || !v.symbol) return startWizard('setup', !!v.again);
  renderMine(v);
  showView('mine');
}

function toggle(btnId: string, boxId: string, other: [string, string]) {
  const box = $(boxId);
  box.hidden = !box.hidden;
  $(btnId).setAttribute('aria-expanded', String(!box.hidden));
  $(other[1]).hidden = true;
  $(other[0]).setAttribute('aria-expanded', 'false');
  return !box.hidden;
}

// ---- wiring ---------------------------------------------------------------------------------------------------
$('draw-btn').addEventListener('click', () => void draw(false));
$('redraw').addEventListener('click', () => void draw(true));
$('to-2').addEventListener('click', () => st.draw && !st.revealing && goStep(2));
$('phrase').addEventListener('input', words);
$('words-form').addEventListener('submit', (e) => void save(e));
$('wiz-back').addEventListener('click', () => {
  if (st.step === 2) goStep(1, -1);
  else if (st.step === 1 && st.mode === 'reset' && st.current?.set) {
    showView('mine');
    focusEl($('mine-title'));
  }
});
$('to-tour').addEventListener('click', () => ($('to-tour').dataset.role === 'finish' ? closeTour() : openTour()));
$('done-skip').addEventListener('click', () => ($('done-skip').dataset.role === 'tour' ? openTour() : closeTour()));
// Enter advances (when focus is on the step itself, e.g. its heading; buttons and the input do it natively).
$('s-setup').addEventListener('keydown', (e) => {
  if (e.key !== 'Enter' || (e.target as HTMLElement).closest('button, a, input, textarea')) return;
  e.preventDefault();
  if (st.step === 1) (st.draw ? $('to-2') : $('draw-btn')).click();
  else if (st.step === 3) $('to-tour').click();
});

$('tour-next').addEventListener('click', () => (st.tour === TOUR_CARDS - 1 ? closeTour() : tourGo(tourMove(st.tour, 1), 1)));
$('tour-prev').addEventListener('click', () => st.tour > 0 && tourGo(tourMove(st.tour, -1), -1));
$('tour-skip').addEventListener('click', closeTour);
$('s-tour').addEventListener('keydown', (e) => {
  const t = e.target as HTMLElement;
  if (e.key === 'ArrowRight' && st.tour < TOUR_CARDS - 1) tourGo(tourMove(st.tour, 1), 1);
  else if (e.key === 'ArrowLeft' && st.tour > 0) tourGo(tourMove(st.tour, -1), -1);
  else if (e.key === 'Enter' && !t.closest('button, a')) $('tour-next').click();
  else if (e.key === 'Escape') closeTour();
});
// Swipe between cards (touch, pen or mouse drag), vertical scrolling untouched.
let sw: { x: number; y: number; t: number; id: number } | null = null;
const vp = $('tour-viewport');
vp.addEventListener('pointerdown', (e) => (sw = { x: e.clientX, y: e.clientY, t: performance.now(), id: e.pointerId }));
vp.addEventListener('pointerup', (e) => {
  if (!sw || sw.id !== e.pointerId) return;
  const d = swipeStep(e.clientX - sw.x, e.clientY - sw.y, performance.now() - sw.t);
  sw = null;
  if (d === -1 && st.tour < TOUR_CARDS - 1) tourGo(tourMove(st.tour, 1), 1);
  else if (d === 1 && st.tour > 0) tourGo(tourMove(st.tour, -1), -1);
});
vp.addEventListener('pointercancel', () => (sw = null));

$('how-check').addEventListener('click', openTour);
$('change-words').addEventListener('click', () => toggle('change-words', 'phrase-box', ['start-over', 'over-box']) && focusEl($('phrase2')));
$('start-over').addEventListener('click', () => toggle('start-over', 'over-box', ['change-words', 'phrase-box']) && focusEl($('over-yes')));
$('over-no').addEventListener('click', () => {
  $('over-box').hidden = true;
  $('start-over').setAttribute('aria-expanded', 'false');
  focusEl($('start-over'));
});
$('over-yes').addEventListener('click', () => {
  $('over-box').hidden = true;
  $('start-over').setAttribute('aria-expanded', 'false');
  startWizard('reset');
});
$('phrase2').addEventListener('input', () => {
  const i = $<HTMLInputElement>('phrase2');
  $('phrase2-count').textContent = counterText(i.value);
  $<HTMLButtonElement>('phrase2-btn').disabled = !phraseState(i.value).ok;
});
$('phrase-form').addEventListener('submit', async (e) => {
  e.preventDefault();
  const err = $('phrase-error');
  err.textContent = '';
  const btn = $<HTMLButtonElement>('phrase2-btn');
  btn.disabled = true;
  try {
    const v = await api({ action: 'phrase', phrase: $<HTMLInputElement>('phrase2').value });
    st.current = v;
    renderMine(v);
    $('phrase-box').hidden = true;
    $('change-words').setAttribute('aria-expanded', 'false');
    focusEl($('mine-title'));
  } catch (x) {
    err.textContent = (x as Error).message;
  } finally {
    btn.disabled = false;
  }
});
$('host').textContent = location.host;
for (const h of document.querySelectorAll('.tour-host')) h.textContent = location.hostname;

void (async () => {
  try {
    render(await api());
  } catch {
    startWizard('setup');
  }
  try {
    const j = (await (await fetch('/api/label-sites')).json()) as { sites: { name: string; domains: string[] }[] };
    for (const s of j.sites)
      for (const d of s.domains) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = `https://${d}/`;
        a.target = '_blank';
        a.rel = 'noopener';
        a.textContent = d;
        const m = document.createElement('span');
        m.className = 'muted';
        m.textContent = ` · ${s.name}: its PageID pill must show your symbol and text`;
        li.append(a, m);
        $('sym-tests').append(li);
      }
  } catch {}
})();
export {};
